import { DateTime } from 'luxon';
import type { User } from '~/common/types';

/**
 * Sentinel filter values for the organization column, used by the filter control.
 * Real orgs are namespaced with ORGANIZATION_VALUE_PREFIX so an org that happens to
 * be named "all" or "none" cannot collide with either sentinel.
 */
export const ALL_ORGANIZATIONS = 'all';
export const NO_ORGANIZATION = 'none';
const ORGANIZATION_VALUE_PREFIX = 'org:';

/** The filter value representing a single organization, e.g. "org:Acme Engineering". */
export function organizationValue(organizationName: string) {
  return `${ORGANIZATION_VALUE_PREFIX}${organizationName}`;
}

/** The organization name a filter value refers to, or null for the sentinels. */
export function organizationNameFromValue(value: string) {
  return value.startsWith(ORGANIZATION_VALUE_PREFIX) ? value.slice(ORGANIZATION_VALUE_PREFIX.length) : null;
}

/** Every org a pending user belongs to, suspended memberships called out. */
export function organizationLabel(user: User) {
  return (
    (user.memberships ?? [])
      .map((m) => (m.status === 'suspended' ? `${m.organizationName} (suspended)` : m.organizationName))
      .join(', ') || '—'
  );
}

/**
 * Matches against the memberships rather than the joined label, so a user who
 * belongs to several orgs is found under each of them and a suspended membership
 * still matches its org name.
 */
export function matchesOrganization(user: User, value: string) {
  const memberships = user.memberships ?? [];

  if (!value || value === ALL_ORGANIZATIONS) return true;
  if (value === NO_ORGANIZATION) return memberships.length === 0;

  const organizationName = organizationNameFromValue(value);

  return organizationName !== null && memberships.some((m) => m.organizationName === organizationName);
}

export function formatCreatedAt(user: User) {
  return DateTime.fromJSDate(new Date(user.createdAt)).toFormat('h:mm a M/d/yyyy');
}

/**
 * Compares epoch millis rather than the formatted text — sorting the display
 * string would order "11/6/2025" after "8/17/2026".
 */
export function compareCreatedAt(a: User, b: User) {
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

/**
 * Options for the organization filter: every org represented in the pending set,
 * busiest first, each labelled with how many pending users it accounts for.
 */
export function buildOrganizationOptions(users: User[]) {
  const counts = new Map<string, number>();
  let withoutOrganization = 0;

  for (const user of users) {
    const names = new Set((user.memberships ?? []).map((m) => m.organizationName));

    if (names.size === 0) {
      withoutOrganization += 1;
      continue;
    }

    for (const name of names) {
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }

  return [
    { value: ALL_ORGANIZATIONS, label: `All organizations (${users.length})` },
    ...(withoutOrganization > 0 ? [{ value: NO_ORGANIZATION, label: `No organization (${withoutOrganization})` }] : []),
    ...[...counts.entries()]
      .sort(([nameA, countA], [nameB, countB]) => countB - countA || nameA.localeCompare(nameB))
      .map(([name, count]) => ({ value: organizationValue(name), label: `${name} (${count})` })),
  ];
}
